import { action, computed, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { assertUnreachable } from '../../../app/shared/common';
import { ModalState } from '../../../app/shared/ModalState';
import { AddProductsToPricelistModalTranslation } from '../../localization/ModuleTranslation';
import { showToast } from '../../ui/Toast/Toast';
import CreatePricelistFormState from '../PricelistForm/CreatePricelistFormState';
import SelectPricelistFormState from './SelectPricelistForm/SelectPricelistFormState';

/** The two ways of adding: onto a list the dealer already has, or onto one created on the spot. */
export const ADD_TO_PRICELIST_MODE_IDS = {
  Existing: 'existing',
  New: 'new',
} as const;

export type AddToPricelistMode = (typeof ADD_TO_PRICELIST_MODE_IDS)[keyof typeof ADD_TO_PRICELIST_MODE_IDS];

interface AddProductsToPricelistResult {
  isSuccess: boolean;
  errorMessage?: string;
  addedProducts?: number;
  pricelistName?: string;
}

export default class AddProductsToPricelistModalState extends ModalState {
  @observable.ref public modes: AddToPricelistMode[] = [];
  @observable public currentMode: AddToPricelistMode | undefined;
  @observable public isPending = false;
  @observable public errorMessage = '';

  public readonly createPricelistForm: CreatePricelistFormState;
  public readonly selectPricelistForm: SelectPricelistFormState;

  public constructor(
    private readonly client: IApiClient,
    public readonly translation: AddProductsToPricelistModalTranslation,
    private readonly getSelectedProductIds: () => string[],
  ) {
    super();
    makeObservable(this);

    this.createPricelistForm = new CreatePricelistFormState(this.client, this.translation.createForm);
    this.selectPricelistForm = new SelectPricelistFormState(this.client, this.translation.selectForm);
  }

  @computed
  public get isLoading(): boolean {
    return this.selectPricelistForm.isLoading;
  }

  public get productCount(): number {
    return this.getSelectedProductIds().length;
  }

  @action.bound
  public changeMode(mode: AddToPricelistMode): void {
    this.currentMode = mode;
    this.errorMessage = '';
  }

  @action.bound
  public async submit(): Promise<void> {
    if (this.isPending) {
      return;
    }

    this.setIsPending(true);
    this.setErrorMessage('');

    const result = await this.submitForm(this.getSelectedProductIds());

    if (result.isSuccess) {
      showToast(
        this.translation.addedToPricelistFormat.interpolate([
          ['count', String(result.addedProducts)],
          ['name', result.pricelistName],
        ]),
      );
      this.close();
    } else if (result.errorMessage) {
      this.setErrorMessage(result.errorMessage);
    }

    this.setIsPending(false);
  }

  protected onAfterOpen(): void {
    this.initialize();
  }

  protected onBeforeClose(): void {
    this.createPricelistForm.reset();
    this.selectPricelistForm.reset();
    this.setErrorMessage('');
  }

  private async submitForm(productModelIds: string[]): Promise<AddProductsToPricelistResult> {
    switch (this.currentMode) {
      case ADD_TO_PRICELIST_MODE_IDS.New: {
        const result = await this.createPricelistForm.submit(productModelIds);
        return result.isSuccess
          ? { isSuccess: true, addedProducts: result.pricelist.totalProducts, pricelistName: result.pricelist.name }
          : result;
      }
      case ADD_TO_PRICELIST_MODE_IDS.Existing:
        return this.selectPricelistForm.submit(productModelIds);
      default:
        assertUnreachable(this.currentMode);
    }
  }

  private async initialize(): Promise<void> {
    await this.selectPricelistForm.load();

    this.setModes(
      this.selectPricelistForm.pricelists.any()
        ? [ADD_TO_PRICELIST_MODE_IDS.Existing, ADD_TO_PRICELIST_MODE_IDS.New]
        : [ADD_TO_PRICELIST_MODE_IDS.New],
    );
  }

  /** The modes and the one in use are settled together, so the modal is never half-decided. */
  @action
  private setModes(modes: AddToPricelistMode[]): void {
    this.modes = modes;
    this.currentMode = modes[0];
  }

  @action
  private setErrorMessage(errorMessage: string): void {
    this.errorMessage = errorMessage;
  }

  @action
  private setIsPending(isPending: boolean): void {
    this.isPending = isPending;
  }
}
